import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';

import { MatSort, Sort } from '@angular/material/sort';

import { MatTableDataSource } from '@angular/material/table';

import { translate } from '@jsverse/transloco';

import { DownloadAsDialogResult } from 'app/modules/collection/components/download-as-dialog/download-as-dialog.component';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetDataSource } from '../../../../models';
import { DocumentService } from '../../../../providers/storage-api/document.service';
import { ProjectService } from '../../../../providers/storage-api/project.service';
import { ReportService } from '../../../../providers/storage-api/report.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import { LoggerService } from '../../../../services/logger.service';
import { SystemService } from '../../../../services/system.service';
import { WidgetLimiterService } from '../../../../services/widget-limiter.service';
import {
  Filter,
  FilterService,
  WidgetQueryOptions,
} from '../../../collection/services/filter.service';
import { DashboardViewComponent } from '../../../dashboard/pages/dashboard-view/dashboard-view.component';
import { AbstractTableWidgetComponent } from '../widget/abstract-table-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import { WidgetChartComponent, WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { SentimentAccuracyChartReport } from '../../services/chart-report';

import { SentimentAccuracyDataItem } from '../../services/chart-report/chart-report-sentiment-accuracy/chart-report-sentiment-accuracy';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface SentimentAccuracyWidgetOptions extends WidgetChartOptions {
  showPhrasesFor: string;
  sentimentField: string;
  filterKey: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-sentiment-accuracy',
  templateUrl: './widget-sentiment-accuracy.component.html',
  styleUrls: ['./widget-sentiment-accuracy.component.scss'],
  standalone: false,
})
export class WidgetSentimentAccuracyComponent
  extends AbstractTableWidgetComponent<SentimentAccuracyWidgetOptions>
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild(MatSort) set tableSort(content: MatSort) {
    if (content) {
      this.dataSource.sort = content;
    }
  }

  @ViewChild('phrasesChart') phrasesChart: WidgetChartComponent;

  public downloadFormats: string[] = ['pptx', 'image', 'csv'];
  public canFilter = true;
  public hasAnalyses = true;
  public displayedColumns = ['sentiment', 'accuracy', 'documentCount', 'incorrectCount'];
  dataSource: MatTableDataSource<SentimentAccuracyDataItem>;
  noData = false;
  sort: Sort;
  private report: SentimentAccuracyChartReport;

  constructor(
    private reportService: ReportService,
    private logger: LoggerService,
    private comparisonService: ComparisonHandlerService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected dialogService: DialogService,
    protected documentService: DocumentService,
    protected alertService: AlertService,
    protected widgetLimiterService: WidgetLimiterService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected projectService: ProjectService,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      filterService,
      dialogService,
      documentService,
      projectService,
      alertService,
      widgetLimiterService,
      interactiveSmartSummaryService,
      dashboardViewComponent,
      widgetComponent,
      systemService,
      accountService,
      authService,
      timezoneService,
      customFieldService,
      commentsHandlerService
    );
    this.sort = {
      active: '',
      direction: 'desc',
    };
  }

  ngOnInit() {
    super.ngOnInit();
    super.updateFilterList();

    this.report = new SentimentAccuracyChartReport(
      this.widget.payload,
      this.reportService,
      this.filterService,
      this.comparisonService,
      this.timezoneService
    );
    this.dataSource = new MatTableDataSource<SentimentAccuracyDataItem>();
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'documentCount':
          return item.total;
        case 'incorrectCount':
          return item.total - item.correct;
        default:
          return item[property];
      }
    };

    this.refresh(false, true);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.widgetLoadController.abort('destroyed');
  }

  public async loadData(initialLoad = false) {
    try {
      const widgetQueryOptions = this.widgetQueryOptions;

      // when widget performs its initial load in the 'Show phrases' state
      if (this.widget.payload.showPhrasesFor) {
        widgetQueryOptions.filters = JSON.parse(JSON.stringify(widgetQueryOptions.filters));
        this.unsetPhrasesDocumentFilter(widgetQueryOptions.filters);
      }

      const query = this.filterService.getQueryForDocuments(widgetQueryOptions, false, true);

      const report = await this.report.executeReportForSentimentAccuracy(
        query,
        this.widget.payload.sentimentField,
        this.widgetDataSource
      );
      this.filteredDocumentCount = this.report.lastDocumentCount;

      if (this.widgetLoadController.signal.aborted) {
        return;
      }

      this.dataSource.data = report.data;

      if (report.summary.unknown) {
        this.widgetInfo = {
          icon: 'warning',
          level: 'WARNING',
          tooltip: translate('widgets.documents-missing-accuracy-markup', {
            count: report.summary.unknown,
          }),
        };
      }

      this.isLoading = false;
      this.dataSourceNotFound = false;
      this.noData = !report.data.length;
    } catch (error) {
      this.logger.error('Error occured during fetching sentiment accuracy data', error);

      if ((error.status = 404)) {
        this.dataSourceNotFound = true;
        this.isLoading = false;
        this.noData = false;
        this.dataSource.data = [];
      }
      return;
    } finally {
      this.loadingFinished.emit(initialLoad);
    }
  }

  async refresh(_reset = false, initialLoad = false, _refreshedDatasource?: WidgetDataSource) {
    super.refresh();

    this.loadingStarted.emit(initialLoad);

    if (this.sentimentScale === 3) {
      await this.widgetLimiterService.execute(async () => {
        if (this.widgetLoadController.signal.aborted) {
          return;
        }

        await this.loadData(initialLoad);
      });
    } else {
      this.isLoading = false;
      this.loadingFinished.emit(initialLoad);
    }
  }

  redraw() {
    if (this.phrasesChart) {
      this.phrasesChart.redraw();
    }
  }

  getCellMenuData(row, column, _extraData) {
    let count;

    switch (column) {
      case 'accuracy':
        count = Math.round(row.accuracy * 100);
        break;
      case 'documentCount':
        count = row.total;
        break;
      case 'incorrectCount':
        count = row.total - row.correct;
        break;
    }

    return {
      row: row.sentiment,
      column,
      count,
    };
  }

  async showDocuments(row, column) {
    const widgetQueryOptions: WidgetQueryOptions = JSON.parse(
      JSON.stringify(this.widgetQueryOptions)
    );

    this.setDocumentFilter(widgetQueryOptions, row, column);

    this.getDocuments(widgetQueryOptions);
  }

  showPhrases(row, column) {
    this.widget.payload.aggregator = 'total';
    this.widget.payload.analyticItem = 'phrases';
    this.widget.payload.graphType = 'column';
    this.widget.payload.type = 'sentimentPolarity';
    this.widget.payload.showPhrasesFor = row;
    this.widget.payload.filterKey = 'sentimentAccuracyWidget';

    this.filteredDocumentCount = undefined;

    this.setDocumentFilter(this.widget.payload, row, column);
  }

  goBack() {
    this.unsetPhrasesDocumentFilter(this.widget.payload.filters);

    this.widget.payload.aggregator = undefined;
    this.widget.payload.analyticItem = undefined;
    this.widget.payload.graphType = undefined;
    this.widget.payload.type = 'sentimentAccuracy';
    this.widget.payload.showPhrasesFor = undefined;

    this.filteredDocumentCount = undefined;
  }

  private setDocumentFilter(payload, row, column) {
    const pushFilterIfNotExists = (filterSet, filter) => {
      if (
        !filterSet.some((f) => f.analyticItem === filter.analyticItem && f.value === filter.value)
      ) {
        filterSet.push(filter);
      }
    };

    if (column === 'accuracy') {
      const polarityFilter: Filter = {
        analyticItem: 'analyses.sentimentPolarity' + this.widget.payload.sentimentScale,
        value: row,
        key: 'sentimentAccuracyWidget',
      };

      pushFilterIfNotExists(payload.filters.analyses.and, polarityFilter);
    } else if (column === 'documentCount') {
      const customSentimentFilter: Filter = {
        analyticItem: 'metadata.' + this.widget.payload.sentimentField,
        value: row,
        key: 'sentimentAccuracyWidget',
      };

      pushFilterIfNotExists(payload.filters.metadata.and, customSentimentFilter);
    } else if (column === 'incorrectCount') {
      const customSentimentFilter: Filter = {
        analyticItem: 'metadata.' + this.widget.payload.sentimentField,
        value: row,
        key: 'sentimentAccuracyWidget',
      };

      pushFilterIfNotExists(payload.filters.metadata.and, customSentimentFilter);

      const polarityFilter: Filter = {
        analyticItem: 'analyses.sentimentPolarity' + this.widget.payload.sentimentScale,
        value: row,
        key: 'sentimentAccuracyWidget',
      };

      pushFilterIfNotExists(payload.filters.analyses.not, polarityFilter);
    }
  }

  private unsetPhrasesDocumentFilter(filters) {
    filters.analyses.and = filters.analyses.and.filter((f) => {
      if (!this.widget.payload.filterKey) {
        return (
          f.analyticItem !== 'analyses.sentimentPolarity' + this.widget.payload.sentimentScale ||
          f.value !== this.widget.payload.showPhrasesFor
        );
      }
      return f.key !== this.widget.payload.filterKey;
    });
    filters.metadata.and = filters.metadata.and.filter((f) => {
      if (!this.widget.payload.filterKey) {
        return (
          f.analyticItem !== `metadata.${this.widget.payload.sentimentField}` ||
          f.value !== this.widget.payload.showPhrasesFor
        );
      }
      return f.key !== this.widget.payload.filterKey;
    });
    filters.analyses.not = this.widget.payload.filters.analyses.not.filter((f) => {
      if (!this.widget.payload.filterKey) {
        return (
          f.analyticItem !== 'analyses.sentimentPolarity' + this.widget.payload.sentimentScale ||
          f.value !== this.widget.payload.showPhrasesFor
        );
      }
      return f.key !== this.widget.payload.filterKey;
    });
  }

  async executeReportForCsv(_result: DownloadAsDialogResult): Promise<any> {
    return this.dataSource.sortData(this.dataSource.data, this.dataSource.sort);
  }

  prepareDataForCsv(reportData) {
    let report = translate('widgets.csv.sentiment-accuracy-header');

    for (const row of reportData) {
      report += `"${row.sentiment}",${Math.round(row.accuracy * 100)}%,${row.total},${
        row.total - row.correct
      }\n`;
    }

    return report;
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    if (this.widget.payload.showPhrasesFor) {
      return this.phrasesChart.exportAsPptx(pptx);
    } else {
      const dataOptions = this.addTitleSlideToPptx(pptx);

      const colW = [6, 2, 2, 2];
      const pptTable = {
        title: this.widget.title,
        rows: [
          [
            {
              text: translate('widgets.pptx.sentiment'),
              options: {
                bold: true,
                fontSize: 16,
              },
            },
            {
              text: translate('widgets.pptx.accuracy'),
              options: {
                bold: true,
                fontSize: 16,
              },
            },
            {
              text: translate('widgets.pptx.document-count'),
              options: {
                bold: true,
                fontSize: 16,
              },
            },
            {
              text: translate('widgets.pptx.incorrect-count'),
              options: {
                bold: true,
                fontSize: 16,
              },
            },
          ],
        ],
      };

      const sortedData = this.dataSource.sortData(this.dataSource.data, this.dataSource.sort);

      sortedData.forEach((row) => {
        const rowGroup = [
          { text: row.sentiment },
          { text: Math.round(row.accuracy * 100) + '%' },
          { text: row.total },
          { text: row.total - row.correct },
        ];

        pptTable.rows.push(rowGroup as any);
      });

      const tableWidth = 12; // in inches

      pptx.addTableSlide(pptTable as any, {
        colW,
        w: tableWidth,
        data: dataOptions,
        autoPage: true,
        lineWeight: -0.3,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      });
    }
  }

  async exportAsImage(): Promise<any> {
    if (this.widget.payload.showPhrasesFor) {
      return this.phrasesChart.exportAsImage();
    } else {
      return super.exportAsImage();
    }
  }

  public get defaultTitle(): string {
    return translate('widgets.accuracy');
  }

  get sentimentScale() {
    return this.widget.payload.sentimentScale;
  }

  actionsDisabled() {
    return this.sentimentScale === 5;
  }

  getCommentsForPoint(_point: any) {
    return [];
  }
}
